import { isEmpty } from 'lodash';
import * as routeBuilder from 'Routes/routeBuilder';
import URI from 'urijs';
import {
  ACTIVITIES_FACET_ID,
  CHILDCARE_FILTER_FACET_ID,
  COUNTRIES_FACET_ID,
  DESTINATIONS_FACET_ID,
  FLIGHT_DURATION_FACET_ID,
  QUERY_QUERY_PARAM,
  SEASON_FACET_ID,
  SORT_BY_PRICE_QUERY_PARAM,
  TYPES_OF_STAY_FACET_ID,
} from 'Utils/faceting/constants';
import {
  customFacetsQueryParamMapping,
  facetsQueryParamMapping,
  formatDepartureDateForStore,
  getDepartureDateDisplayValue,
  synchronizeChildcareQPWithKidsQP,
  serializeKidsToQueryParam,
} from 'Utils/faceting/facetingUtils';
import {
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_KIDS_DELIMITER,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
export const FACET_QUERY_PARAMS = [
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_QUERY_PARAM,
  QUERY_TRANSPORT,
  customFacetsQueryParamMapping[FLIGHT_DURATION_FACET_ID],
  facetsQueryParamMapping[ACTIVITIES_FACET_ID],
  facetsQueryParamMapping[CHILDCARE_FILTER_FACET_ID],
  facetsQueryParamMapping[COUNTRIES_FACET_ID],
  facetsQueryParamMapping[DESTINATIONS_FACET_ID],
  facetsQueryParamMapping[SEASON_FACET_ID],
  facetsQueryParamMapping[TYPES_OF_STAY_FACET_ID],
  SORT_BY_PRICE_QUERY_PARAM,
];
export type SearchCriteria = {
  departureCity: string;
  departureDate: number | null | undefined;
  duration: number | null | undefined;
  keywords: string;
  kids: Array<any>;
  kidsValidationState: Array<ValidationState>;
  query: SearchQuery;
};

const getChildcareFacetFilterQueryParams = (args: {
  activeFacetsQueries?: {};
  criteriaKeysToPersist: Array<string>;
  currentQueryParams: QueryParams;
  searchCriteria: SearchCriteria;
}) => {
  const { activeFacetsQueries, criteriaKeysToPersist, currentQueryParams, searchCriteria } = args;

  if (criteriaKeysToPersist.includes(QUERY_KIDS) && searchCriteria.departureDate) {
    const departureDateQPCurrentValue = currentQueryParams[QUERY_DEPARTURE_DATE] ?? '';
    const kidsQPCurrentValue = currentQueryParams[QUERY_KIDS] ?? '';
    return synchronizeChildcareQPWithKidsQP({
      childcareFacetQueryParam:
        activeFacetsQueries?.[facetsQueryParamMapping[CHILDCARE_FILTER_FACET_ID]] ?? '',
      departureDate: searchCriteria.departureDate,
      departureDateQueryParam: Array.isArray(departureDateQPCurrentValue)
        ? departureDateQPCurrentValue.join('')
        : departureDateQPCurrentValue,
      kids: searchCriteria.kids,
      kidsQueryParam: Array.isArray(kidsQPCurrentValue)
        ? kidsQPCurrentValue.join(QUERY_KIDS_DELIMITER)
        : kidsQPCurrentValue,
    });
  }

  return undefined;
};

const computeQueryParamsForUrl = (args: {
  criteriaKeysToPersist: Array<string>;
  searchCriteria: SearchCriteria;
}) => {
  const { criteriaKeysToPersist, searchCriteria } = args;
  const formattedDate =
    searchCriteria.departureDate &&
    getDepartureDateDisplayValue(formatDepartureDateForStore(searchCriteria.departureDate, ''));
  const searchQuery = searchCriteria.query ? searchCriteria.query.label : '';
  return {
    [QUERY_DEPARTURE_DATE]:
      (criteriaKeysToPersist.includes(QUERY_DEPARTURE_DATE) && formattedDate) || undefined,
    [QUERY_DURATION]:
      (criteriaKeysToPersist.includes(QUERY_DURATION) && searchCriteria.duration) || undefined,
    [QUERY_TRANSPORT]:
      (criteriaKeysToPersist.includes(QUERY_TRANSPORT) && searchCriteria.departureCity) ||
      undefined,
    [QUERY_QUERY_PARAM]:
      (criteriaKeysToPersist.includes(QUERY_QUERY_PARAM) &&
        (searchCriteria.keywords || searchQuery)) ||
      undefined,
    [QUERY_KIDS]:
      (criteriaKeysToPersist.includes(QUERY_KIDS) &&
        searchCriteria.kids.length > 0 &&
        serializeKidsToQueryParam({
          kids: searchCriteria.kids,
          kidsValidationState: searchCriteria.kidsValidationState,
        })) ||
      undefined,
  };
};

export const navigateToSearchPage = (args: {
  activeCustomFacetsQueries?: {};
  activeFacetsQueries?: {};
  criteriaKeysToPersist: Array<string>;
  router: any;
  searchCriteria: SearchCriteria;
  searchPageRouteName?: string;
}) => {
  const {
    activeCustomFacetsQueries,
    activeFacetsQueries,
    criteriaKeysToPersist,
    router,
    searchCriteria,
    searchPageRouteName,
  } = args;
  let queryParams = computeQueryParamsForUrl({
    criteriaKeysToPersist,
    searchCriteria,
  });
  const childcareFacetFilterQueryParams = getChildcareFacetFilterQueryParams({
    activeFacetsQueries,
    criteriaKeysToPersist,
    currentQueryParams: router.query,
    searchCriteria,
  });
  queryParams = {
    ...queryParams,
    ...activeCustomFacetsQueries,
    ...activeFacetsQueries,
    ...(childcareFacetFilterQueryParams && {
      [facetsQueryParamMapping[CHILDCARE_FILTER_FACET_ID]]: childcareFacetFilterQueryParams,
    }),
    ...(childcareFacetFilterQueryParams === '' && {
      [facetsQueryParamMapping[CHILDCARE_FILTER_FACET_ID]]: undefined,
    }),
  };
  // NOTE: URI.setQuery() does not remove existing queries
  const searchPageUrl = new URI(router.asPath).setQuery(queryParams);
  const previousPathname = searchPageUrl.pathname();
  const pathname = searchPageRouteName ? routeBuilder.urlPattern(searchPageRouteName) : null;

  if (pathname) {
    searchPageUrl.pathname(pathname);
  }

  const isShallow = searchPageUrl.pathname() === previousPathname;
  router.replace(searchPageUrl.toString(), undefined, {
    shallow: isShallow,
  });
};
export const navigateToResortPage = (args: {
  query: SearchQuery;
  router: any;
  searchCriteria: SearchCriteria;
}) => {
  const { query, router, searchCriteria } = args;
  const queryParams = computeQueryParamsForUrl({
    criteriaKeysToPersist: [QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT, QUERY_KIDS],
    searchCriteria,
  });
  const resortPageUrl = new URI(router.asPath).pathname(query.link).setQuery(queryParams);
  router.push(resortPageUrl.toString());
};
export const currentUrlContainsSearchQueryParam = (queryParams: any): boolean => {
  return !isEmpty(queryParams[QUERY_QUERY_PARAM]);
};
export const currentUrlHasNoQueryParams = (queryParams: any): boolean => {
  return FACET_QUERY_PARAMS.every((queryKey) => isEmpty(queryParams[queryKey]));
};
export const getFacetsAsString = (
  filters: Array<{
    id: string;
    name: string;
  }>,
  facetCategoryId: string,
): string | null => {
  const facetsInCategory = filters.filter((filter) => filter.id === facetCategoryId);

  if (facetsInCategory.length === 0) {
    return null;
  }

  return facetsInCategory.map((filter) => filter.name).join('|');
};
